"use client";

import { FC, useState, useEffect, MouseEvent } from "react";
import { useRouter } from "next/navigation";

import { useAppDispatch } from "@/redux/store";
import { useStyles } from "./styles";
import { User } from "@/types/user";
import { Nullable, PageType } from "@/types/common";
import { UserFollow, UserProfile } from "@/types/social";
import { createFollow, deleteFollow } from "@/redux/actions";
import SecondaryButton from "@/components/common/SecondaryButton";

interface FollowButtonProps {
  observer: User | null;
  user: User | UserProfile | null;
  userFollow: Nullable<UserFollow>;
  pageType: PageType;
  disabled?: boolean;
  className?: string;
}

const FollowButton: FC<FollowButtonProps> = ({
  observer,
  user,
  userFollow,
  pageType,
  className,
  disabled,
}) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const [userFollowed, setUserFollowed] = useState(Boolean(userFollow));
  const [localUserFollow, setlocalUserFollow] = useState(userFollow);
  const [loading, setLoading] = useState(false);

  const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (!loading) {
      setLoading(true);
      if (!observer) {
        router.push("/register");
        return;
      }

      if (user) {
        const newUserFollowed = !userFollowed;
        setUserFollowed(newUserFollowed);

        if (newUserFollowed) {
          // Follow user

          const reduxResult: any = await dispatch(
            createFollow({ followingUserId: user.id, pageType })
          ).unwrap();
          if (reduxResult) {
            router.refresh();
            setlocalUserFollow(reduxResult);
          } else {
            setUserFollowed(!newUserFollowed);
          }
        } else if (!newUserFollowed) {
          // Unfollow user

          const reduxResult: any = await dispatch(
            deleteFollow({ followId: localUserFollow?.id || 0, pageType })
          ).unwrap();
          if (reduxResult) {
            router.refresh();
            setlocalUserFollow(null);
          } else {
            setUserFollowed(!newUserFollowed);
          }
        }
      }
    }
  };

  useEffect(() => {
    if (loading) {
      let ratingInterval: Nullable<ReturnType<typeof setInterval>> =
        setInterval(() => {
          setLoading(false);
        }, 1000);

      return () => {
        if (ratingInterval) {
          clearInterval(ratingInterval);
          ratingInterval = null;
        }
      };
    }
  }, [loading]);

  return (
    <SecondaryButton
      disabled={disabled}
      onClick={handleClick}
      className={className || classes.container}
    >
      {userFollowed ? "Following" : "Follow"}
    </SecondaryButton>
  );
};

export default FollowButton;
